import type { WireTypes } from '@flakiness/server/common/wireTypes.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlChangeEvent, SlRadioGroup } from '@shoelace-style/shoelace';
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { createPullsDataProvider } from './data-table-providers/pullsProvider.js';
import { forgeUtils } from './forgeUtils.js';
import { markdownStyles } from './markdown.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';

const pageSchema = {
  prState: URLState.option<string>({
    name: 'pr_state',
    default: 'open',
    encode: filter => filter,
    decode: filter => filter ?? 'all',
  }),
  testState: URLState.option<string>({
    name: 'test_state',
    default: 'all',
    encode: filter => filter,
    decode: filter => filter ?? 'all',
  }),
};

@customElement('page-project-pulls')
export class PageProjectPulls extends LitElement {
  static url(options: { orgSlug: string, projectSlug: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'pulls'].join('/'), undefined, options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/pulls',
        '/:org/:project/pulls/'
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-pulls
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-pulls>
      `),
    }];
  }

  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;

  private _urlState = new URLState(this, pageSchema);

  private _dataTableProviderTask = new Task(this, {
    args: () => [
      this._project, this._snapshotId, this._urlState.state.prState, this._urlState.state.testState,
    ] as const,
    task: async ([project, snapshotId, prState, testState], { signal }) => {
      CHECK(project && snapshotId);
      if (!prState || !['all', 'open', 'closed', 'merged'].includes(prState))
        prState = 'all';
      if (!testState || !['all', 'tested', 'untested'].includes(testState))
        testState = 'all';

      return createPullsDataProvider({
        project,
        snapshotId,
        timezone: project.timezone,
        prState: prState as any,
        testState: testState as any,
      });
    }
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug, this._project] as const,
    metadata: ([orgSlug, projectSlug, project]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: forgeUtils.pullRequestLabelPlural(project),
    }) : undefined,
  });

  override render() {
    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"pulls"}
      ></project-header>
      <project-body wide>
        <h-box>
          <sl-radio-group size=small value=${this._urlState.state.prState} @sl-change=${(e: SlChangeEvent) => {
            const prState = (e.target as SlRadioGroup).value;
            this._urlState.update({ prState });
          }}>
            <sl-radio-button value="all">All</sl-radio-button>
            <sl-radio-button value="open">Opened</sl-radio-button>
            <sl-radio-button value="closed">Closed</sl-radio-button>
            <sl-radio-button value="merged">Merged</sl-radio-button>
          </sl-radio-group>
          <sl-radio-group size=small value=${this._urlState.state.testState} @sl-change=${(e: SlChangeEvent) => {
            this._urlState.update({ testState: (e.target as SlRadioGroup).value });
          }}>
            <sl-radio-button value="all">All</sl-radio-button>
            <sl-radio-button value="tested">Tested</sl-radio-button>
            <sl-radio-button value="untested">Untested</sl-radio-button>
          </sl-radio-group>
        </h-box>
        <data-table
          .pageNumber=${0}
          .pageSize=${20}
          .provider=${this._dataTableProviderTask.value}
          .noDataMessage=${`No ${forgeUtils.pullRequestLabelPlural(this._project)}`}
        ></data-table>
      </project-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [...markdownStyles, pageStyles, linkStyles, css`
  `]
}
